import React, { useState, useEffect } from 'react';
import { StickyNote, Plus, Search, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateNoteModal from '../../../components/admin/crm/CreateNoteModal';
import EditNoteModal from '../../../components/admin/crm/EditNoteModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, by_entity: { clients: 0, leads: 0, opportunities: 0, contacts: 0 } });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchNotes();
    fetchCount();
    fetchStats();
  }, [pagination, searchTerm, entityTypeFilter]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const entityParam = entityTypeFilter ? `&entity_type=${entityTypeFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/notes?skip=${pagination.skip}&limit=${pagination.limit}${searchParam}${entityParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch notes');

      const data = await response.json();
      setNotes(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notes',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const entityParam = entityTypeFilter ? `?entity_type=${entityTypeFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/notes/count${entityParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch count');

      const data = await response.json();
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/crm/notes/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleEdit = (note) => {
    setSelectedNote(note);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete note');

      toast({
        title: 'Success',
        description: 'Note deleted successfully'
      });
      fetchNotes();
      fetchCount();
      fetchStats();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete note',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (noteId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/notes/${noteId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore note');

      toast({
        title: 'Success',
        description: 'Note restored successfully'
      });
      fetchNotes();
      fetchStats();
    } catch (error) {
      console.error('Error restoring note:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore note',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < totalCount) {
      setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <StickyNote className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Notes</h2>
              <p className="text-sm text-[#6C757D]">Manage notes and annotations for CRM entities</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Note
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Notes</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Clients</p>
              <p className="text-3xl font-bold text-[#00B4D8]">{stats.by_entity.clients}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Leads</p>
              <p className="text-3xl font-bold text-[#00B4D8]">{stats.by_entity.leads}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Opportunities</p>
              <p className="text-3xl font-bold text-[#00B4D8]">{stats.by_entity.opportunities}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Contacts</p>
              <p className="text-3xl font-bold text-[#00B4D8]">{stats.by_entity.contacts}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                placeholder="Search notes..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>

            {/* Entity Type Filter */}
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Entity Types</option>
              <option value="client">Clients</option>
              <option value="lead">Leads</option>
              <option value="opportunity">Opportunities</option>
              <option value="contact">Contacts</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Notes List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading notes...</p>
          </CardContent>
        </Card>
      ) : notes.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm || entityTypeFilter ? 'No notes found matching your filters.' : 'No notes yet. Add your first note!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card 
              key={note.id} 
              className={note.is_deleted ? 'opacity-50' : ''}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-[#2B2D42]">
                        {note.subject}
                      </h3>
                      <span className="px-2 py-1 text-xs bg-[#00B4D8] bg-opacity-10 text-[#00B4D8] rounded-full">
                        {note.related_to_type}
                      </span>
                      {note.is_deleted && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Deleted
                        </span>
                      )}
                    </div>
                    {note.description && (
                      <p className="text-sm text-[#6C757D] mb-3 whitespace-pre-wrap">
                        {note.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm text-[#6C757D]">
                      <div>
                        <span className="font-medium">Created:</span> {formatDate(note.created_at)}
                      </div>
                      {note.updated_at && note.updated_at !== note.created_at && (
                        <div>
                          <span className="font-medium">Updated:</span> {formatDate(note.updated_at)}
                        </div>
                      )}
                    </div>
                    {note.tags && note.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {note.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {note.is_deleted ? (
                      <Button
                        onClick={() => handleRestore(note.id)}
                        variant="outline"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleEdit(note)}
                          variant="outline"
                          size="sm"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(note.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <Button
            onClick={handlePrevPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={pagination.skip + pagination.limit >= totalCount}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <CreateNoteModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchNotes();
          fetchCount();
          fetchStats();
        }}
      />

      <EditNoteModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedNote(null);
        }}
        onSuccess={() => {
          setIsEditModalOpen(false);
          setSelectedNote(null);
          fetchNotes();
          fetchStats();
        }}
        note={selectedNote}
      />
    </div>
  );
};

export default Notes;
